// frontend/src/components/loi/recordFacts.ts
/**
 * ⭐ THE CORRECTION NOTE (spec 1 and 3.9 - never tasked by the plan, Ruling 29; built by the 113th on his answer "build
 *   next"). His decision 2026-09-29: when his version contradicts a source, DO IT, AND NOTE IT TO HIM - one line to him,
 *   never in the document. The letter prints what its boxes hold; this says where that differs from the record the letter
 *   is about. The spec's words: "Your 42,000 SF differs from the record's 40,000 SF."
 * ⛔ v1 adds the note, not an editor (spec 3.9): a correction to the record itself goes through the record's own edit
 *   path, where his entry ranks first.
 */

/** The building facts as the record holds them - set by a record page, never by the chat or a document read. */
export interface RecordFacts {
  address?: string | null;
  city?: string | null;
  zip?: string | null;
  buildingSf?: number | null;
  acres?: number | null;
  lotSf?: number | null;
}

/**
 * What a record page hands the letter: the six facts, a blank as a blank (a 0 SF, 0 acres or 0 lot SF is no size at all).
 * ⚠️ The acres come only from a column that is ALWAYS acres (`lot_size_acres`) - never from a `lot_size` whose unit the
 *   record does not state (the 114th measured it: 38 of the 41 deal-side property rows carry a `lot_size`, none an acreage).
 */
export const recordFactsOf = (p: RecordFacts): RecordFacts => ({
  address: p.address || null,
  city: p.city || null,
  zip: p.zip || null,
  buildingSf: typeof p.buildingSf === 'number' && p.buildingSf > 0 ? p.buildingSf : null,
  acres: typeof p.acres === 'number' && p.acres > 0 ? p.acres : null,
  lotSf: typeof p.lotSf === 'number' && p.lotSf > 0 ? p.lotSf : null,
});

/** An acreage as the note prints it - so two figures that print the same are never called different. */
const acresShown = (a: number) => a.toLocaleString('en-US', { maximumFractionDigits: 3 });

/** Case and spacing are how it was typed, not what it says. */
const sameText = (a: string, b: string) => a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * One line per fact the letter states differently from the record, in the letter's own order. ⛔ Nothing where either
 * side is blank: a box he emptied is left out of the letter, not contradicted, and a fact the record lacks is filled in.
 */
export const correctionNotes = (
  record: RecordFacts | undefined,
  letter: { address: string; city: string; zip: string | null; buildingSf: number | null; lotSizeAcres?: number | null; lotSf?: number | null },
): string[] => {
  if (!record) return [];
  const notes: string[] = [];
  const text = (label: string, mine: string | null, theirs: string | null | undefined) => {
    if (mine && mine.trim() && theirs && theirs.trim() && !sameText(mine, theirs)) {
      notes.push(`Your ${label}, ${mine.trim()}, differs from the record's ${theirs.trim()}.`);
    }
  };
  text('address', letter.address, record.address);
  text('city', letter.city, record.city);
  text('ZIP', letter.zip, record.zip);
  if (letter.buildingSf && record.buildingSf && letter.buildingSf !== record.buildingSf) {
    notes.push(`Your ${letter.buildingSf.toLocaleString('en-US')} SF differs from the record's ${record.buildingSf.toLocaleString('en-US')} SF.`);
  }
  // ⭐ The 114th: the chat's acres reach the letter on a record page now (his answer 2026-10-01), so the note speaks for them.
  if (letter.lotSizeAcres && record.acres && acresShown(letter.lotSizeAcres) !== acresShown(record.acres)) {
    notes.push(`Your ${acresShown(letter.lotSizeAcres)} acres differs from the record's ${acresShown(record.acres)} acres.`);
  }
  // ⭐ The 114th's review: his lot SF reaches the letter too ("(+/- 12,197 SF)" after the acres) - land SF on a master record.
  if (letter.lotSf && record.lotSf && letter.lotSf !== record.lotSf) {
    notes.push(`Your lot SF, ${letter.lotSf.toLocaleString('en-US')}, differs from the record's ${record.lotSf.toLocaleString('en-US')}.`);
  }
  return notes;
};
